// eslint-disable-next-line import/no-extraneous-dependencies
import { Anthropic } from '@anthropic-ai/sdk';
import { useEffect, useState } from 'react';

const anthropic = new Anthropic({
  apiKey:
    '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',
  dangerouslyAllowBrowser: true
});

interface Delta {
  type: string;
  text: string;
}

interface ClaudeMessagesProps {
  asHtml?: boolean;
  type?: 'pdf' | 'product';
  fileBase: string;
  tokens?: number;
  stream?: boolean;
  prevChat?: string[];
  prompt: string;
}

export const useClaudeMessages = ({
  type = 'pdf',
  fileBase,
  tokens,
  stream: asStream = true,
  prompt
}: Partial<ClaudeMessagesProps>) => {
  const [stream, setStream] = useState('');
  const [error, setError] = useState({
    message: '',
    status: 0
  });
  const [loading, setLoading] = useState(false);

  const asyncResponse = async ({
    asHtml = false,
    type: moduleType = 'pdf',
    fileBase: data,
    tokens: max_tokens = 1024,
    // prevChat,
    prompt: text
  }: ClaudeMessagesProps) => {
    setLoading(true);
    setStream('');

    try {
      const streamResponse = await anthropic.messages.create({
        model: 'claude-3-5-sonnet-latest',
        max_tokens,
        stream: asStream,
        messages: [
          {
            role: 'user',
            ...(moduleType === 'pdf' && {
              content: [
                {
                  type: 'text',
                  text: `You are a helpful assistant. Below is the content of a PDF document. Use it to answer questions, summarize sections, explain concepts, or extract key information as requested.`
                },
                {
                  type: 'text',
                  text: `--- BEGIN PDF CONTENT ---\n${data}\n--- END PDF CONTENT ---`
                }
              ]
            }),
            ...(moduleType === 'product' && {
              content: [
                {
                  type: 'text',
                  text: `You are a highly skilled product enablement assistant. Below is a combination of product metadata and a structured sales kit. Use this information to summarize product features, articulate value propositions, identify ideal customer profiles, address common objections, and generate sales-ready insights tailored to commercial foodservice environments. Focus on clarity, accuracy, and persuasive business value.`
                },
                {
                  type: 'text',
                  text: `--- BEGIN PRODUCT DATA CONTENT ---\n${data}\n--- END PRODUCT DATA CONTENT ---`
                }
              ]
            })
          },
          // {
          //   role: 'user',
          //   content:
          //     prevChat?.map(prevTxt => ({
          //       type: 'text',
          //       text: prevTxt
          //     })) || []
          // },
          {
            role: 'user',
            content: !asHtml
              ? ''
              : `Return the response in HTML format. Do not include any extra text outside of the HTML. Do not use h1, h2, h3, h4, h5, h6 or other large heading elements. Keep the formatting clean and regular like ChatGPT or Claude, using only paragraph text, lists, and subtle emphasis when needed and use <b> or <i> for bold or italic text where needed.`
          },
          {
            role: 'user',
            content: text // the user's question about the PDF
          }
        ]
      });

      let finalMessage = '';

      if (
        asStream &&
        streamResponse &&
        typeof (streamResponse as any)[Symbol.asyncIterator] === 'function'
      ) {
        for await (const messageStream of streamResponse as AsyncIterable<any>) {
          if (messageStream.type === 'content_block_delta') {
            const response = messageStream.delta as Delta;

            finalMessage += response?.text || '';

            setStream(prev => (prev || '') + (response?.text || ''));
          }
        }
      } else if (streamResponse && !asStream) {
        // Handle non-stream response
        // Assuming streamResponse has a 'content' property with the message text
        const message = (streamResponse as any).content?.[0]?.text;
        if (typeof message === 'string') {
          finalMessage = message;
          setStream(message);
        }
      }

      return finalMessage;
    } catch (err) {
      setError({
        message:
          err?.message?.message || err?.message || 'Something went wrong',
        status: err?.status || 500
      });
      return '';
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileBase && prompt) {
      asyncResponse({
        type,
        fileBase,
        tokens,
        prompt
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileBase, type, tokens, prompt]);

  const onRegenerate = () => {
    const oldStream = stream;
    setLoading(true);
    setStream('');
    setError({
      message: '',
      status: 0
    });

    asyncResponse({
      type,
      fileBase,
      tokens,
      prompt: `${prompt}\n\n Previous chat: ${oldStream}. Regenerate the response. Do not repeat the same response.`
    });
  };

  return { asyncResponse, onRegenerate, error, stream, loading };
};
